import React, { useState, useRef, useEffect } from "react";
import ArrowDown from "src/components/icons/miscs/ArrowDown";
import ArrowUp from "src/components/icons/miscs/ArrowUp";
import BookIcon from "src/assets/images/book.svg";
import SwitchIcon from "src/assets/images/switch.svg";
import AddIcon from "src/components/icons/common/add-icon";

interface CampaignDisplayItemProps {
  keyName: string;
  content: string;
  tag: string;
  isEditable?: boolean;
  onContentChange?: (newContent: string) => void;
}

const CampaignDisplayItem: React.FC<CampaignDisplayItemProps> = ({
  keyName,
  content,
  tag,
  isEditable = false,
  onContentChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showArrow, setShowArrow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      const LineClampHeight = 80;
      const contentHeight = contentRef.current.scrollHeight;
      setShowArrow(contentHeight > LineClampHeight);
    }
  }, [content]);

  const handleContentChange = (newContent: string) => {
    if (onContentChange) {
      onContentChange(newContent);
    }
  };

  return (
    <div className="flex items-start mb-2">
      <div className="flex flex-col w-full border rounded-lg border-gray-300">
        <div className="flex items-center justify-between p-2 border-b border-gray-300">
          <div className="flex items-start gap-2">
            <h3 className="text-left text-sm sm:text-base font-bold">
              {keyName}
            </h3>
          </div>
        </div>
        
        {content && (
          <div className="p-3 py-2 text-sm relative">
            <div className="flex gap-2 items-start">
              <div
                ref={contentRef}
                className={`text-gray-700 text-justify tracking-tight flex-1 ${
                  isExpanded ? '' : 'line-clamp-4'
                }`}
              >
                {isEditable ? (
                  <div className="campaign-content-editable space-y-2">
                    <div dangerouslySetInnerHTML={{ __html: content }} />
                  </div>
                ) : (
                  <div 
                    className="campaign-content"
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                )}
              </div>
              {showArrow && (
                <button 
                  onClick={() => setIsExpanded(!isExpanded)} 
                  className="top-2 right-3 mt-1 flex-shrink-0"
                >
                  {isExpanded ? <ArrowUp /> : <ArrowDown />}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Editable field components
interface EditableFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'textarea';
  placeholder?: string;
}

const EditableField: React.FC<EditableFieldProps> = ({
  label,
  value,
  onChange,
  type = 'text',
  placeholder
}) => {
  return (
    <div className="flex gap-2 items-start mb-2">
      <p className="font-bold w-40 mt-1">{label}:</p>
      {type === 'textarea' ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          rows={3}
        />
      ) : (
                <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      )}
    </div>
  );
};

interface EditableListFieldProps {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
}

const EditableListField: React.FC<EditableListFieldProps> = ({
  label,
  values,
  onChange
}) => {
  const addItem = () => {
    onChange([...values, '']);
  };

  const removeItem = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, value: string) => {
    const newValues = [...values];
    newValues[index] = value;
    onChange(newValues);
  };

  return (
    <div className="flex gap-2 items-start mb-2">
      <p className="font-bold w-40 mt-1">{label}:</p>
      <div className="flex-1">
        {values.map((value, index) => (
          <div key={index} className="flex gap-2 mb-1">
            <input
              type="text"
              value={value}
              onChange={(e) => updateItem(index, e.target.value)}
              className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder={`${label} item`}
            />
            <button
              onClick={() => removeItem(index)}
              className="text-red-500 hover:text-red-700 px-2"
              type="button"
            >
              ×
            </button>
          </div>
        ))}
        <button
          onClick={addItem}
          className="text-blue-500 hover:text-blue-700 text-sm"
          type="button"
        >
          + Add {label.toLowerCase()}
        </button>
      </div>
    </div>
  );
};

// New component for editing key-value pairs (Budget Split, Daily Spend Cap)
// Fixed EditableKeyValueField component with stable keys
// interface EditableKeyValueFieldProps {
//   label: string;
//   values: { [key: string]: string };
//   onChange: (values: { [key: string]: string }) => void;
// }

// interface KeyValueItem {
//   id: string;
//   key: string;
//   value: string;
// }

// const EditableKeyValueField: React.FC<EditableKeyValueFieldProps> = ({
//   label,
//   values,
//   onChange
// }) => {
//   // Convert object to array with stable IDs for React keys
//   const [items, setItems] = React.useState<KeyValueItem[]>(() => {
//     return Object.entries(values).map(([key, value], index) => ({
//       id: `item-${index}-${Date.now()}`,
//       key,
//       value
//     }));
//   });

//   // Update items when values prop changes (but only if it's a different structure)
//   React.useEffect(() => {
//     const currentKeys = items.map(item => item.key).sort();
//     const newKeys = Object.keys(values).sort();
    
//     // Only update if the structure has actually changed
//     if (JSON.stringify(currentKeys) !== JSON.stringify(newKeys)) {
//       setItems(Object.entries(values).map(([key, value], index) => ({
//         id: `item-${index}-${Date.now()}`,
//         key,
//         value
//       })));
//     } else {
//       // Update values without changing structure
//       setItems(prevItems => 
//         prevItems.map(item => ({
//           ...item,
//           value: values[item.key] || item.value
//         }))
//       );
//     }
//   }, [values]);

//   // Convert items array back to object and call onChange
//   const updateParent = (newItems: KeyValueItem[]) => {
//     const newValues: { [key: string]: string } = {};
//     newItems.forEach(item => {
//       if (item.key.trim() !== '') {
//         newValues[item.key] = item.value;
//       }
//     });
//     onChange(newValues);
//   };

//   const addItem = () => {
//     const newItem: KeyValueItem = {
//       id: `item-${Date.now()}-${Math.random()}`,
//       key: '',
//       value: ''
//     };
//     const newItems = [...items, newItem];
//     setItems(newItems);
//     updateParent(newItems);
//   };

//   const removeItem = (id: string) => {
//     const newItems = items.filter(item => item.id !== id);
//     setItems(newItems);
//     updateParent(newItems);
//   };

//   const updateItemKey = (id: string, newKey: string) => {
//     const newItems = items.map(item =>
//       item.id === id ? { ...item, key: newKey } : item
//     );
//     setItems(newItems);
//     updateParent(newItems);
//   };

//   const updateItemValue = (id: string, newValue: string) => {
//     const newItems = items.map(item =>
//       item.id === id ? { ...item, value: newValue } : item
//     );
//     setItems(newItems);
//     updateParent(newItems);
//   };

//   return (
//     <div className="flex gap-2 items-start mb-2">
//       <p className="font-bold w-40 mt-1">{label}:</p>
//       <div className="flex-1 pn_scroller overflow-y-scroll">
//         {items.map((item) => (
//           <div key={item.id} className="flex gap-2 mb-1">
//             <input
//               type="text"
//               value={item.key}
//               onChange={(e) => updateItemKey(item.id, e.target.value)}
//               className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
//               placeholder="Key (e.g., Email Marketing)"
//             />
//             <input
//               type="text"
//               value={item.value}
//               onChange={(e) => updateItemValue(item.id, e.target.value)}
//               className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
//               placeholder="Value (e.g., $1000)"
//             />
//             <button
//               onClick={() => removeItem(item.id)}
//               className="text-red-500 hover:text-red-700 px-2"
//               type="button"
//             >
//               ×
//             </button>
//           </div>
//         ))}
//         <button
//           onClick={addItem}
//           className="text-blue-500 hover:text-blue-700 text-sm mb-2"
//           type="button"
//         >
//           + Add {label.toLowerCase()}
//         </button>
//       </div>
//     </div>
//   );
// };

// Fixed EditableKeyValueField component with stable keys
interface EditableKeyValueFieldProps {
  label: string;
  values: { [key: string]: string };
  onChange: (values: { [key: string]: string }) => void;
}

interface KeyValueItem {
  id: string;
  key: string;
  value: string;
}

const EditableKeyValueField: React.FC<EditableKeyValueFieldProps> = ({
  label,
  values,
  onChange
}) => {
  // Convert object to array with stable IDs for React keys
  const [items, setItems] = React.useState<KeyValueItem[]>(() => {
    return Object.entries(values).map(([key, value], index) => ({
      id: `item-${index}-${Date.now()}`,
      key,
      value
    }));
  });

  // Update items when values prop changes from external source
  React.useEffect(() => {
    // Only sync if this is an external change (not from our own updates)
    const currentValidKeys = items.filter(item => item.key.trim() !== '').map(item => item.key).sort();
    const newKeys = Object.keys(values).sort();
    
    // Check if this is a real external change or our own update
    const isExternalChange = JSON.stringify(currentValidKeys) !== JSON.stringify(newKeys);
    
    if (isExternalChange) {
      setItems(Object.entries(values).map(([key, value], index) => ({
        id: `item-${index}-${Date.now()}`,
        key,
        value
      })));
    }
  }, [values]);

  // Convert items array back to object and call onChange
  const updateParent = (newItems: KeyValueItem[]) => {
    const newValues: { [key: string]: string } = {};
    newItems.forEach(item => {
      if (item.key.trim() !== '') {
        newValues[item.key] = item.value;
      }
    });
    onChange(newValues);
  };

  const addItem = () => {
    const newItem: KeyValueItem = {
      id: `item-${Date.now()}-${Math.random()}`,
      key: '',
      value: ''
    };
    const newItems = [...items, newItem];
    setItems(newItems);
    updateParent(newItems);
  };

  const removeItem = (id: string) => {
    const newItems = items.filter(item => item.id !== id);
    setItems(newItems);
    updateParent(newItems);
  };

  const updateItemKey = (id: string, newKey: string) => {
    const newItems = items.map(item =>
      item.id === id ? { ...item, key: newKey } : item
    );
    setItems(newItems);
    updateParent(newItems);
  };

  const updateItemValue = (id: string, newValue: string) => {
    const newItems = items.map(item =>
      item.id === id ? { ...item, value: newValue } : item
    );
    setItems(newItems);
    updateParent(newItems);
  };

  return (
    <div className="flex gap-2 items-start mb-2">
      <p className="font-bold w-40 mt-1">{label}:</p>
      <div className="flex-1 pn_scroller overflow-y-scroll">
        {items.map((item) => (
          <div key={item.id} className="flex gap-2 mb-1">
            <input
              type="text"
              value={item.key}
              onChange={(e) => updateItemKey(item.id, e.target.value)}
              className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Key (e.g., Email Marketing)"
            />
            <input
              type="text"
              value={item.value}
              onChange={(e) => updateItemValue(item.id, e.target.value)}
              className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Value (e.g., $1000)"
            />
            <button
              onClick={() => removeItem(item.id)}
              className="text-red-500 hover:text-red-700 px-2"
              type="button"
            >
              ×
            </button>
          </div>
        ))}
        <button
          onClick={addItem}
          className="text-blue-500 hover:text-blue-700 text-sm mb-2"
          type="button"
        >
          + Add {label.toLowerCase()}
        </button>
      </div>
    </div>
  );
};

// New component for editing alert rules
interface AlertRule {
  action: string;
  kpi: string;
  condition: string;
  value: string;
  duration: string;
}

interface EditableAlertRulesProps {
  label: string;
  rules: AlertRule[];
  onChange: (rules: AlertRule[]) => void;
}

const EditableAlertRules: React.FC<EditableAlertRulesProps> = ({
  label,
  rules,
  onChange
}) => {
  const addRule = () => {
    onChange([...rules, { action: '', kpi: '', condition: '', value: '', duration: '' }]);
  };

  const removeRule = (index: number) => {
    onChange(rules.filter((_, i) => i !== index));
  };

  const updateRule = (index: number, field: keyof AlertRule, value: string) => {
    const newRules = [...rules];
    newRules[index] = { ...newRules[index], [field]: value };
    onChange(newRules);
  };

  return (
    <div className="flex gap-2 items-start mb-2">
      <p className="font-bold w-40 mt-1">{label}:</p>
      <div className="flex-1">
        {rules.map((rule, index) => (
          <div key={index} className="flex flex-col border rounded p-2 mb-2">
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={rule.action}
                onChange={(e) => updateRule(index, 'action', e.target.value)}
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Action (e.g., Send alert)"
              />
              <button
                onClick={() => removeRule(index)}
                className="text-red-500 hover:text-red-700 px-2"
                type="button"
              >
                ×
              </button>
            </div>
            <div className="flex flex-col gap-2 mb-2">
              <input
                type="text"
                value={rule.kpi}
                onChange={(e) => updateRule(index, 'kpi', e.target.value)}
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="KPI (e.g., CTR)"
              />
              <select
                value={rule.condition}
                onChange={(e) => updateRule(index, 'condition', e.target.value)}
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Select condition</option>
                <option value="<">Less than</option>
                <option value=">">Greater than</option>
                <option value="=">Equal to</option>
                <option value="<=">Less than or equal</option>
                <option value=">=">Greater than or equal</option>
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <input
                type="text"
                value={rule.value}
                onChange={(e) => updateRule(index, 'value', e.target.value)}
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Value (e.g., 2%)"
              />
              <input
                type="text"
                value={rule.duration}
                onChange={(e) => updateRule(index, 'duration', e.target.value)}
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Duration (e.g., 24 hours)"
              />
            </div>
          </div>
        ))}
        <button
          onClick={addRule}
          className="text-blue-500 hover:text-blue-700 text-sm"
          type="button"
        >
          + Add rule
        </button>
      </div>
    </div>
  );
};

type CampaignTemplateReportProps = {
  campaignData: any;
  disabled?: boolean;
  onSave?: (updatedData: any) => void;
};

interface ReportItem {
  title: string;
  summary: string;
  body: string;
  subsections?: Record<string, ReportItem>;
}

interface CampaignSection {
  key: string;
  title: string;
  content: string;
}

const CampaignTemplateReport: React.FC<CampaignTemplateReportProps> = ({
  campaignData,
  disabled = false,
  onSave,
}) => {
  const [open, setOpen] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [campaignDataInternal, setCampaignDataInternal] = useState(campaignData);
  const [editableData, setEditableData] = useState(campaignDataInternal);
  const [newItemTitle, setNewItemTitle] = useState("");
  const [newItemSummary, setNewItemSummary] = useState("");
  const [newItemBody, setNewItemBody] = useState("");
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [customSections, setCustomSections] = useState<CampaignSection[]>([]);

  useEffect(() => {
    if (onSave) {
      onSave(campaignDataInternal);
    }
  }, [campaignDataInternal, onSave]);

  // Early return after hooks
  if (!campaignDataInternal) return null;

  const {
    version,
    title,
    created_date,
    created_by,
    status,
    campaign_overview,
    strategy,
    budgeting,
    kpis,
    alert_rules,
    monitoring,
    notifications,
    attribution,
    post_sales_support,
    burning_questions,
    reporting,
    automation
  } = isEditing ? editableData : campaignDataInternal;

  // Helper function to safely get array values
  const getArrayValue = (arr: any[]): string => {
    return Array.isArray(arr) && arr.length > 0 ? arr.join(", ") : "Not specified";
  };

  // Helper function to safely get string values
  const getStringValue = (value: any): string => {
    return value && value.toString().trim() !== "" ? value.toString() : "Not specified";
  };

  // Helper function to format object as key-value pairs
  const formatObjectAsHtml = (obj: any, label: string): string => {
    if (!obj || typeof obj !== 'object') return `<p><strong>${label}:</strong> Not specified</p>`;
    
    const entries = Object.entries(obj)
      .map(([key, value]) => `<li><strong>${key}:</strong> ${value}</li>`)
      .join("");
    
    return `
      <div>
        <strong>${label}:</strong>
        <ul class="list-disc ml-6 mt-1">
          ${entries}
        </ul>
      </div>
    `;
  };

  // Helper function to parse budget split and daily spend cap
  const parseBudgetObject = (value: any): { [key: string]: string } => {
    if (typeof value === 'object' && value !== null) {
      return value;
    }
    if (typeof value === 'string') {
      try {
        // Try to parse JSON string
        return JSON.parse(value);
      } catch {
        // If not JSON, try to parse key-value format like ["Email Marketing":"$1000","Facebook":"$4000"]
        const matches = value.match(/["']([^"']+)["']\s*:\s*["']([^"']+)["']/g);
        if (matches) {
          const result: { [key: string]: string } = {};
          matches.forEach((match: string) => {
            const [, key, val] = match.match(/["']([^"']+)["']\s*:\s*["']([^"']+)["']/) || [];
            if (key && val) {
              result[key] = val;
            }
          });
          return result;
        }
      }
    }
    return {};
  };

  // Update editable data functions
  const updateCampaignOverview = (field: string, value: string) => {
    setEditableData({
      ...editableData,
      campaign_overview: {
        ...editableData.campaign_overview,
        [field]: value
      }
    });
  };

  const updateStrategy = (field: string, value: string | string[]) => {
    setEditableData({
      ...editableData,
      strategy: {
        ...editableData.strategy,
        [field]: value
      }
    });
  };

  const updateBudgeting = (field: string, value: any) => {
    setEditableData({
      ...editableData,
      budgeting: {
        ...editableData.budgeting,
        [field]: value
      }
    });
  };

  const updateKPIs = (field: string, value: string[]) => {
    setEditableData({
      ...editableData,
      kpis: {
        ...editableData.kpis,
        [field]: value
      }
    });
  };

  const updateAlertRules = (value: AlertRule[]) => {
    setEditableData({
      ...editableData,
      alert_rules: value
    });
  };

  const updateMonitoring = (field: string, value: string) => {
    setEditableData({
      ...editableData,
      monitoring: {
        ...editableData.monitoring,
        [field]: value
      }
    });
  };

  const updateNotifications = (field: string, value: string[]) => {
    setEditableData({
      ...editableData,
      notifications: {
        ...editableData.notifications,
        [field]: value
      }
    });
  };

  const updateAttribution = (field: string, value: string[]) => {
    setEditableData({
      ...editableData,
      attribution: {
        ...editableData.attribution,
        [field]: value
      }
    });
  };

  const updatePostSalesSupport = (field: string, value: string) => {
    setEditableData({
      ...editableData,
      post_sales_support: {
        ...editableData.post_sales_support,
        [field]: value
      }
    });
  };

  const updateBurningQuestions = (value: string[]) => {
    setEditableData({
      ...editableData,
      burning_questions: value
    });
  };

  const updateReporting = (field: string, value: string) => {
    setEditableData({
      ...editableData,
      reporting: {
        ...editableData.reporting,
        [field]: value
      }
    });
  };

  const updateAutomation = (field: string, value: string) => {
    setEditableData({
      ...editableData,
      automation: {
        ...editableData.automation,
        [field]: value
      }
    });
  };

  // Render editable sections
  const renderEditableSection = (sectionKey: string, title: string) => {
    switch (sectionKey) {
      case "1": // Campaign Overview
        return (
          <div className="space-y-2">
            <EditableField
              label="Name"
              value={campaign_overview?.name || ''}
              onChange={(value) => updateCampaignOverview('name', value)}
            />
            <EditableField
              label="Description"
              value={campaign_overview?.description || ''}
              onChange={(value) => updateCampaignOverview('description', value)}
              type="textarea"
            />
          </div>
        );

      case "2": // Strategy
        return (
          <div className="space-y-2">
            <EditableListField
              label="Main Goals"
              values={strategy?.main_goals || ['']}
              onChange={(value) => updateStrategy('main_goals', value)}
            />
            <EditableListField
              label="Target Segments"
              values={strategy?.target_segments || ['']}
              onChange={(value) => updateStrategy('target_segments', value)}
            />
            <EditableListField
              label="Channels"
              values={strategy?.channels || ['']}
              onChange={(value) => updateStrategy('channels', value)}
            />
          </div>
        );

      case "3": // Budgeting
        return (
          <div className="space-y-2">
            <EditableField
              label="Total Budget"
              value={budgeting?.total_budget || ''}
              onChange={(value) => updateBudgeting('total_budget', value)}
            />
            <EditableKeyValueField
              label="Budget Split"
              values={parseBudgetObject(budgeting?.budget_split)}
              onChange={(value) => updateBudgeting('budget_split', value)}
            />
            <EditableKeyValueField
              label="Daily Spend Cap"
              values={parseBudgetObject(budgeting?.daily_spend_cap)}
              onChange={(value) => updateBudgeting('daily_spend_cap', value)}
            />
          </div>
        );

      case "4": // KPIs
        return (
          <div className="space-y-2">
            <EditableListField
              label="Primary KPIs"
              values={kpis?.primary_kpis || ['']}
              onChange={(value) => updateKPIs('primary_kpis', value)}
            />
            <EditableListField
              label="Important KPIs"
              values={kpis?.important_kpis || ['']}
              onChange={(value) => updateKPIs('important_kpis', value)}
            />
            <EditableListField
              label="Tracked KPIs"
              values={kpis?.tracked_kpis || ['']}
              onChange={(value) => updateKPIs('tracked_kpis', value)}
            />
          </div>
        );

      case "5": // Alert Rules
        return (
          <div className="space-y-2">
            <EditableAlertRules
              label="Alert Rules"
              rules={alert_rules || []}
              onChange={updateAlertRules}
            />
          </div>
        );

      case "6": // Monitoring
        return (
          <div className="space-y-2">
            <EditableField
              label="Lead Follow-up"
              value={monitoring?.lead_follow_up || monitoring?.monitor_lead_follow_ups || ''}
              onChange={(value) => updateMonitoring('lead_follow_up', value)}
            />
            <EditableField
              label="Max Follow-up Delay"
              value={monitoring?.max_follow_up_delay || monitoring?.maximum_allowed_delay_for_follow_ups || ''}
              onChange={(value) => updateMonitoring('max_follow_up_delay', value)}
            />
          </div>
        );

      case "7": // Notifications
        return (
          <div className="space-y-2">
            <EditableListField
              label="Recipients"
              values={notifications?.recipients || ['']}
              onChange={(value) => updateNotifications('recipients', value)}
            />
          </div>
        );

      case "8": // Attribution
        return (
          <div className="space-y-2">
            <EditableListField
              label="Models"
              values={attribution?.models || ['']}
              onChange={(value) => updateAttribution('models', value)}
            />
          </div>
        );

      case "9": // Post Sales Support
        return (
          <div className="space-y-2">
            <EditableField
              label="Forecast Hours"
              value={post_sales_support?.forecast_hours || post_sales_support?.forecast_post_sales_support_hours || ''}
              onChange={(value) => updatePostSalesSupport('forecast_hours', value)}
            />
          </div>
        );

      case "10": // Burning Questions
        return (
          <div className="space-y-2">
            <EditableListField
              label="Questions"
              values={burning_questions || ['']}
              onChange={updateBurningQuestions}
            />
          </div>
        );

      case "11": // Reporting
        return (
          <div className="space-y-2">
            <EditableField
              label="Visualization"
              value={reporting?.visualization || ''}
              onChange={(value) => updateReporting('visualization', value)}
            />
            <EditableField
              label="Adjustments Needed"
              value={reporting?.adjustments_needed || ''}
              onChange={(value) => updateReporting('adjustments_needed', value)}
            />
          </div>
        );

      case "12": // Automation
        return (
          <div className="space-y-2">
            <EditableField
              label="Enabled"
              value={automation?.enabled || ''}
              onChange={(value) => updateAutomation('enabled', value)}
            />
          </div>
        );

      default:
        return null;
    }
  };

  // Transform campaign data into sections for display
  const campaignSections: CampaignSection[] = [];

  if (campaign_overview) {
    campaignSections.push({
      key: "1",
      title: "Campaign Overview",
      content: isEditing ? '' : `
        <div class="space-y-2">
          <p><strong>Name:</strong> ${getStringValue(campaign_overview.name)}</p>
          <p><strong>Description:</strong> ${getStringValue(campaign_overview.description)}</p>
        </div>
      `,
    });
  }

  if (strategy) {
    campaignSections.push({
      key: "2",
      title: "Strategy",
      content: isEditing ? '' : `
        <div class="space-y-2">
          <div class="flex gap-2">
            <p class="font-bold w-40">Main Goals:</p>
            <ul class="list-disc list-inside">
              ${strategy.main_goals?.map((item:any) => `<li>${item}</li>`).join("") || '<li>Not specified</li>'}
            </ul>
          </div>
          <div class="flex gap-2">
            <p class="font-bold w-40">Target Segments:</p>
            <ul class="list-disc list-inside">
              ${strategy.target_segments?.map((item:any) => `<li>${item}</li>`).join("") || '<li>Not specified</li>'}
            </ul>
          </div>
          <div class="flex items-start gap-2">
            <p class="font-bold w-40">Channels:</p>
            <ul class="list-disc list-inside">
              ${strategy.channels?.map((item:any) => `<li>${item}</li>`).join("") || '<li>Not specified</li>'}
            </ul>
          </div>
        </div>
      `,
    });
  }

  if (budgeting) {
    const budgetSplitContent = budgeting.budget_split && typeof budgeting.budget_split === 'object'
      ? formatObjectAsHtml(budgeting.budget_split, "Budget Split")
      : '<p><strong>Budget Split:</strong> Not specified</p>';

    const dailySpendCapContent = budgeting.daily_spend_cap && typeof budgeting.daily_spend_cap === 'object'
      ? formatObjectAsHtml(budgeting.daily_spend_cap, "Daily Spend Caps")
      : `<p><strong>Daily Spend Caps:</strong> ${getStringValue(budgeting.daily_spend_cap)}</p>`;

    campaignSections.push({
      key: "3",
      title: "Budgeting",
      content: isEditing ? '' : `
        <div class="space-y-3">
          <p><strong>Total Budget:</strong> ${getStringValue(budgeting.total_budget)}</p>
          ${budgetSplitContent}
          ${dailySpendCapContent}
        </div>
      `,
    });
  }

  if (kpis) {
    campaignSections.push({
      key: "4",
      title: "KPIs",
      content: isEditing ? '' : `
        <div class="space-y-2">
          <p><strong>Primary KPIs:</strong> ${getArrayValue(kpis.primary_kpis)}</p>
          <p><strong>Important KPIs:</strong> ${getArrayValue(kpis.important_kpis)}</p>
          <p><strong>Tracked KPIs:</strong> ${getArrayValue(kpis.tracked_kpis)}</p>
        </div>
      `,
    });
  }

  if (alert_rules && Array.isArray(alert_rules) && alert_rules.length > 0) {
    const alertsContent = alert_rules
      .map((rule: any, index: number) => `
        <li>
          <strong>Rule ${index + 1}:</strong> ${getStringValue(rule.action)} 
          if ${getStringValue(rule.kpi)} ${getStringValue(rule.condition)} 
          ${getStringValue(rule.value)} for ${getStringValue(rule.duration)}
        </li>
      `)
      .join("");

    campaignSections.push({
      key: "5",
      title: "Alert Rules",
      content: isEditing ? '' : `
        <div>
          <ul class="list-disc ml-6 space-y-1">
            ${alertsContent}
          </ul>
        </div>
      `,
    });
  }

  if (monitoring) {
    campaignSections.push({
      key: "6",
      title: "Monitoring",
      content: isEditing ? '' : `
        <div class="space-y-2">
          <p><strong>Lead Follow-up:</strong> ${getStringValue(monitoring.lead_follow_up || monitoring.monitor_lead_follow_ups)}</p>
          <p><strong>Max Follow-up Delay:</strong> ${getStringValue(monitoring.max_follow_up_delay || monitoring.maximum_allowed_delay_for_follow_ups)} ${monitoring.max_follow_up_delay ? 'hours' : ''}</p>
        </div>
      `,
    });
  }

  if (notifications) {
    campaignSections.push({
      key: "7",
      title: "Notifications",
      content: isEditing ? '' : `
        <div class="flex gap-2">
          <p class="font-bold w-40">Recipients:</p>
          <ul class="list-disc list-inside">
            ${notifications.recipients?.map((item:any) => `<li>${item}</li>`).join("") || '<li>Not specified</li>'}
          </ul>
        </div>
      `,
    });
  }

  if (attribution) {
    campaignSections.push({
      key: "8",
      title: "Attribution Models",
      content: isEditing ? '' : `
        <div class="space-y-2">
          <p><strong>Models:</strong> ${getArrayValue(attribution.models)}</p>
        </div>
      `,
    });
  }

  if (post_sales_support) {
    campaignSections.push({
      key: "9",
      title: "Post Sales Support",
      content: isEditing ? '' : `
        <div class="space-y-2">
          <p><strong>Forecast Hours:</strong> ${getStringValue(post_sales_support.forecast_hours || post_sales_support.forecast_post_sales_support_hours)}</p>
        </div>
      `,
    });
  }

  if (burning_questions && Array.isArray(burning_questions) && burning_questions.length > 0) {
    const questionsContent = burning_questions
      .map((q: string, index: number) => `<li><strong>Q${index + 1}:</strong> ${getStringValue(q)}</li>`)
      .join("");

    campaignSections.push({
      key: "10",
      title: "Burning Questions",
      content: isEditing ? '' : `
        <div>
          <ul class="list-disc ml-6 space-y-1">
            ${questionsContent}
          </ul>
        </div>
      `,
    });
  }

  if (reporting) {
    campaignSections.push({
      key: "11",
      title: "Reporting",
      content: isEditing ? '' : `
        <div class="space-y-2">
          <p><strong>Visualization:</strong> ${getStringValue(reporting.visualization)}</p>
          <p><strong>Adjustments Needed:</strong> ${getStringValue(reporting.adjustments_needed)}</p>
        </div>
      `,
    });
  }

  if (automation) {
    campaignSections.push({
      key: "12",
      title: "Automation",
      content: isEditing ? '' : `
        <div class="space-y-2">
          <p><strong>Enabled:</strong> ${getStringValue(automation.enabled)}</p>
        </div>
      `,
    });
  }

  // Combine original sections with custom sections
  const allSections = [...campaignSections, ...customSections];

  const handleEdit = () => {
    // Load editableData from latest campaignDataInternal
    setEditableData(campaignDataInternal);
    setIsEditing(true);
  };

  const handleSave = () => {
    // Commit changes into the "true" data
    setCampaignDataInternal(editableData);
   
    setIsEditing(false);
    if (onSave) {
      onSave(editableData); // 🔥 send to parent immediately on save
    }
  };
  
  const handleCancel = () => {
    // Discard edits
    setEditableData(campaignDataInternal);
    setIsEditing(false);
  };

  // const handleContinue = () => {
  //   // ✅ At this point, campaignDataInternal already has the latest saved edits
  //   console.log("Final data ready to be submitted:", campaignDataInternal);
     
  //   // You can now send this to API later or move to next step
  // };

  return (
    <div
      className={`rounded-lg w-full mb-[70px] p-2 h-[90vh] ${
        open ? 'flex-[0_0_500px] max-w-full lg:max-w-[460px]' : 'max-w-full lg:max-w-[460px] h-fit'
      }`}
    >
      <div
        className={`px-1 py-1 ${
          open ? "w-full h-full" : "w-full h-auto"
        } bg-opacity-50 rounded-lg`}
      >
        <div className="flex flex-col gap-y-2">
          <div className="flex items-start gap-x-2">
            <div className="cursor-pointer py-1">
              <img src={BookIcon} alt="book" className="w-full object-contain min-w-[20px]" />
            </div>
            <div className="flex-auto">
              <div className="flex items-center justify-between">
                <h2 className="text-base lg:text-lg font-semibold">
                  {title || "Campaign Template"}
                </h2>
                <div
                  onClick={() => setOpen(!open)}
                  className="cursor-pointer rounded border border-[#DFE3E8] p-1"
                >
                  <img src={SwitchIcon} alt="toggle" className="w-[20px]" />
                </div>
              </div>
              {open && <h4 className="mb-4">Campaign sections overview.</h4>}
            </div>
          </div>
        </div>
        
        {open && (
          <div className="h-[90%] flex flex-col overflow-y-hidden">
            <div className="h-[75%] sm:h-[80%] pn_scroller overflow-y-auto overflow-x-hidden pr-1">
              {allSections.map((section) => (
                <div key={section.key} className="flex items-start mb-2 ">
                  <div className="flex flex-col w-full border rounded-lg border-gray-300">
                    <div className="flex items-center justify-between p-2 border-b border-gray-300">
                      <div className="flex items-start gap-2">
                        <h3 className="text-left text-sm sm:text-base font-bold">
                          {section.title}
                        </h3>
                      </div>
                    </div>
                    
                    <div className="p-3 py-2 text-sm">
                      {isEditing ? (
                        renderEditableSection(section.key, section.title)
                      ) : (
                        <div 
                          className="campaign-content text-gray-700 text-justify tracking-tight"
                          dangerouslySetInnerHTML={{ __html: section.content }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end gap-2 mt-3">
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancel}
                    className="cursor-pointer rounded-lg px-2 py-1 border border-gray-300 hover:bg-gray-100 text-[#373D3F]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="cursor-pointer rounded-lg px-2 py-1 border bg-green-500 hover:bg-green-600 text-white"
                  >
                    Save
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleEdit}
                    className="cursor-pointer rounded-lg px-2 py-1 border border-secondary-500 hover:bg-foundationOrange-100 text-[#373D3F]"
                  >
                    Edit
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignTemplateReport;
       